import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { ToastProvider } from './components/Toast/ToastContext';
import { ToastBridge } from './components/Toast/ToastContext';
import reportWebVitals from './reportWebVitals';
import { PublicClientApplication, BrowserUtils } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './config/authConfig';
import { validateConfig, performExtendedValidation } from './utils/validateConfig';

// Early exit if running inside an MSAL popup - prevents nested popup errors
// The popup window should only handle the auth response, not render the full app
if (BrowserUtils.isInPopup()) {
  // Let MSAL handle the popup response - don't initialize the full app
  const msalPopupInstance = new PublicClientApplication(msalConfig);
  msalPopupInstance.initialize().then(() => {
    msalPopupInstance.handleRedirectPromise();
  });
  // Stop further execution - MSAL will close the popup after handling the response
  throw new Error('Running in popup - MSAL will handle auth response');
}

// Validate configuration before initializing the application
try {
  validateConfig();
  performExtendedValidation();
} catch (error) {
  // Configuration validation failed - show error to user safely (no XSS risk)
  console.error('Failed to initialize application due to configuration errors');

  // Create error display elements safely using DOM manipulation
  const errorDiv = document.createElement('div');
  errorDiv.style.cssText = 'padding: 40px; font-family: system-ui; max-width: 800px; margin: 0 auto;';

  const heading = document.createElement('h1');
  heading.style.color = '#d32f2f';
  heading.textContent = '⚠️ Configuration Error';

  const message1 = document.createElement('p');
  message1.textContent = 'The application failed to start due to missing or invalid configuration.';

  const message2 = document.createElement('p');
  message2.textContent = 'Please check the browser console for detailed error information.';

  const errorPre = document.createElement('pre');
  errorPre.style.cssText = 'background: #f5f5f5; padding: 20px; border-radius: 4px; overflow-x: auto;';
  errorPre.textContent = error instanceof Error ? error.message : 'Unknown error';

  const devNote = document.createElement('p');
  const strong = document.createElement('strong');
  strong.textContent = 'For developers: ';
  devNote.appendChild(strong);
  devNote.appendChild(document.createTextNode('Check your '));
  const code = document.createElement('code');
  code.textContent = '.env';
  devNote.appendChild(code);
  devNote.appendChild(document.createTextNode(' file and ensure all required environment variables are set.'));

  errorDiv.appendChild(heading);
  errorDiv.appendChild(message1);
  errorDiv.appendChild(message2);
  errorDiv.appendChild(errorPre);
  errorDiv.appendChild(devNote);

  document.body.innerHTML = '';
  document.body.appendChild(errorDiv);

  throw error;
}

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Export for use in API auth helper
export { msalInstance };

// Initialize MSAL and render the app
async function initializeApp() {
  // Initialize MSAL instance (required in MSAL v3+)
  await msalInstance.initialize();

  // Handle redirect promise to ensure authentication state is properly set
  // This is critical for preventing login loops
  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      // Set the active account if we have a response
      msalInstance.setActiveAccount(response.account);
    } else {
      // Check if we have accounts and set the first one as active
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
  } catch (error) {
    console.error('Error handling redirect:', error);
  }

  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <ToastProvider>
          <ToastBridge />
          <App />
        </ToastProvider>
      </MsalProvider>
    </React.StrictMode>
  );
}

// Start the application
initializeApp().catch((error) => {
  console.error('Failed to initialize MSAL:', error);
  document.body.innerHTML = '<div style="padding: 40px; font-family: system-ui;"><h1>⚠️ Authentication Error</h1><p>Failed to initialize authentication system. Please refresh the page.</p></div>';
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
